import React, { useEffect, useState } from "react";
import { CreditCard } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import bannerImg from "../../Images/Payment.svg";
import googlepay from "../../Images/google-pay 1.svg";
import applepay from "../../Images/apple.svg";
import paypal from "../../Images/paypol.svg";
import stripeLogo from "../../Images/stripe.png";
import ToastMessage from "../../config/ToastMessage";
import { API_URL } from "../../config/api";

export default function PaymentGateway() {
  const [selectedPayment, setSelectedPayment] = useState("card");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    amount: "",
    cardNumber: "",
    expiry: "",
    cvv: "",
  });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Get course data from previous page
  const courseData = location.state || {};
  const {
    course_id,
    schedule_id,
    amount,
    course_name,
    duration,
    lectures,
    skill_level,
    language,
    start_date,
    end_date,
  } = courseData;
  
  
  // ✅ Redirect if not authenticated
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      setToast({
        show: true,
        message: "Please log in to access the payment page.",
        type: "error",
      });
      setTimeout(() => navigate("/login"), 1800);
    }
  }, [navigate]);

  // ✅ Redirect if course data is missing
  useEffect(() => {
    if (!course_id) {
      setToast({
        show: true,
        message: "Please select a course first.",
        type: "error",
      });
      setTimeout(() => navigate("/courses"), 2000);
    }
  }, [course_id, navigate]);

  // ✅ Prefill amount
  useEffect(() => {
    if (amount) {
      setFormData((prev) => ({ ...prev, amount }));
    }
  }, [amount]);

  // ✅ Helper to format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "TBD";
    const d = new Date(dateStr);
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  // ✅ Card Payment Handler
  const handleCardPayment = async () => {
    try {
      const token = localStorage.getItem("access_token");

      if (!token || !schedule_id) {
        setToast({
          show: true,
          message: "Please log in and select a course schedule!",
          type: "error",
        });
        return;
      }

      setLoading(true);

      // Send only schedule_id
      const response = await fetch(`${API_URL}/enroll-course/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          schedule_id, // ✅ only schedule_id
        }),
      });

      const data = await response.json();
      setLoading(false);

      if (response.ok) {
        setToast({
          show: true,
          message: "✅ Enrollment successful!",
          type: "success",
        });
        
        // ✅ Pass course details including enrollment_id and dates to thank you page
        setTimeout(() => navigate("/thankyou", {
          state: {
            enrollment_id: data.enrollment_id, // ✅ Added enrollment_id from response
            course_id: data.course_id,
            schedule_id: data.schedule_id,
            course_name,
            amount,
            duration,
            lectures,
            skill_level,
            language,
            start_date,
            end_date,
          }
        }), 1500);
      } else {
        setToast({
          show: true,
          message: `⚠️ ${data.detail || "Enrollment failed!"}`,
          type: "error",
        });
      }
    } catch (err) {
      console.error("Enroll error:", err);
      setLoading(false);
      setToast({
        show: true,
        message: "⚠️ Something went wrong while enrolling!",
        type: "error",
      });
    }
  };

  // ✅ Stripe Payment Handler
  const handleStripePayment = async () => {
    if (!formData.name || !formData.email || !formData.amount) {
      setToast({
        show: true,
        message: "Please fill in all fields before proceeding!",
        type: "error",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/request-payment/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          amount: parseFloat(formData.amount),
          course_id,
          schedule_id, // Include schedule_id for reference
        }),
      });
      const data = await response.json();
      setLoading(false);

      if (data.status === "success") {
        // ✅ Store course data in localStorage for Stripe redirect callback
        localStorage.setItem("pending_course_data", JSON.stringify({
          course_name,
          amount,
          duration,
          lectures,
          skill_level,
          language,
          start_date,
          end_date,
          schedule_id, // Store schedule_id for later enrollment
        }));
        window.location.href = data.link;
      } else {
        setToast({
          show: true,
          message: `❌ ${data.message || "Something went wrong"}`,
          type: "error",
        });
      }
    } catch (err) {
      console.error("Stripe payment error:", err);
      setLoading(false);
      setToast({
        show: true,
        message: "⚠️ Something went wrong while sending payment link.",
        type: "error",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Banner */}
      <div className="relative w-full h-[350px]">
        <img
          src={bannerImg}
          alt="Payment Banner"
          className="w-full h-full object-cover"
        />
        <div className="absolute bottom-10 left-12 text-white">
          <h2 className="text-3xl font-bold">Payment Gateway</h2>
          <p className="text-sm mt-2">
            Course Start: {formatDate(start_date)} - End: {formatDate(end_date)}
          </p>
          <p className="text-sm">Duration: {duration}</p>
        </div>
      </div>

      {/* Payment Section */}
      <div className="max-w-6xl mx-auto py-10 px-4 grid grid-cols-1 lg:grid-cols-2 gap-10">
        {/* Payment Methods */}
        <div className="w-full flex p-6 bg-white shadow-md rounded-lg">
          <div className="w-1/3 flex flex-col gap-6 border-r pr-4">
            {[
              {
                type: "card",
                label: "Pay with Card",
                icon: <CreditCard size={18} />,
              },
              { type: "paypal", label: "PayPal", image: paypal },
              { type: "applepay", label: "Apple Pay", image: applepay },
              { type: "gpay", label: "Google Pay", image: googlepay },
              { type: "stripe", label: "Stripe", image: stripeLogo },
            ].map((method) => (
              <label
                key={method.type}
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="radio"
                  name="payment"
                  checked={selectedPayment === method.type}
                  onChange={() => setSelectedPayment(method.type)}
                  className="accent-blue-500"
                />
                {method.icon ? (
                  <span className="flex items-center gap-2 text-gray-700 font-medium">
                    {method.icon} {method.label}
                  </span>
                ) : (
                  <img
                    src={method.image}
                    alt={method.label}
                    className={`${
                      method.type === "stripe" ? "w-6 h-6" : "w-14"
                    } object-contain`}
                  />
                )}
              </label>
            ))}
          </div>

          {/* Payment Form */}
          <div className="w-2/3 pl-6">
            {selectedPayment === "card" && (
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">
                  Pay with Credit / Debit Card
                </h3>
                <div className="flex flex-col gap-4">
                  <input
                    type="text"
                    placeholder="Cardholder Name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-400 outline-none"
                  />
                  <input
                    type="text"
                    placeholder="Card Number"
                    value={formData.cardNumber}
                    onChange={(e) =>
                      setFormData({ ...formData, cardNumber: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-400 outline-none"
                  />
                  <div className="flex gap-3">
                    <input
                      type="text"
                      placeholder="MM/YY"
                      value={formData.expiry}
                      onChange={(e) =>
                        setFormData({ ...formData, expiry: e.target.value })
                      }
                      className="w-1/2 border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-400 outline-none"
                    />
                    <input
                      type="password"
                      placeholder="CVV"
                      value={formData.cvv}
                      onChange={(e) =>
                        setFormData({ ...formData, cvv: e.target.value })
                      }
                      className="w-1/2 border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-400 outline-none"
                    />
                  </div>
                  <button
                    onClick={handleCardPayment}
                    disabled={loading}
                    className={`w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 rounded-md transition-all ${
                      loading ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                  >
                    {loading ? "Processing..." : "Proceed to Pay with Card"}
                  </button>
                </div>
              </div>
            )}

            {selectedPayment === "stripe" && (
              <div className="flex flex-col items-center justify-center p-6 w-full">
                <img src={stripeLogo} alt="Stripe" className="w-24 mb-3" />
                <p className="text-gray-700 text-center text-sm mb-4">
                  Enter your details and we'll send a{" "}
                  <span className="font-semibold">Stripe Payment Link</span> to
                  your email.
                </p>
                <div className="w-full flex flex-col gap-4">
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-400 outline-none"
                  />
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-400 outline-none"
                  />
                  <input
                    type="number"
                    placeholder="Amount (in USD)"
                    value={formData.amount}
                    onChange={(e) =>
                      setFormData({ ...formData, amount: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-400 outline-none"
                  />
                  <button
                    onClick={handleStripePayment}
                    disabled={loading}
                    className={`w-full bg-[#635BFF] hover:bg-[#4B46D0] text-white font-semibold py-2 rounded-md transition-all ${
                      loading ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                  >
                    {loading ? "Sending Mail..." : "Send Stripe Payment Link"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Course Info */}
        <div className="bg-white shadow-lg rounded-xl p-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-6">
            {course_name || "Selected Course"}
          </h3>
          <div className="space-y-6">
            <div className="flex items-center justify-between border-b pb-2">
              <span className="text-gray-600 font-medium">Duration:</span>
              <span className="text-gray-900 font-semibold">{duration}</span>
            </div>
            <div className="flex items-center justify-between border-b pb-2">
              <span className="text-gray-600 font-medium">Lectures:</span>
              <span className="text-gray-900 font-semibold">{lectures}</span>
            </div>
            <div className="flex items-center justify-between border-b pb-2">
              <span className="text-gray-600 font-medium">Skill Level:</span>
              <span className="text-gray-900 font-semibold">{skill_level}</span>
            </div>
            <div className="flex items-center justify-between border-b pb-2">
              <span className="text-gray-600 font-medium">Language:</span>
              <span className="text-gray-900 font-semibold">{language}</span>
            </div>
            <div className="flex items-center justify-between pt-2">
              <span className="text-gray-600 font-medium">Price:</span>
              <span className="text-blue-600 font-bold text-lg">${amount}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast.show && (
        <ToastMessage
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
}